import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider.jsx'
import { supabase } from '../data/supabase.js'

const SUPER_ADMIN_LOGIN = 'willian.costa7465@gmail.com'

export default function SuperAdminPage() {
  const { session, ready, enterEmpresa, exitEmpresa, logout } = useAuth()
  const navigate = useNavigate()
  const [empresas, setEmpresas] = useState([])
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const [form, setForm] = useState({ nome: '', email: '', plano: 'basic' })
  const [confirmarExclusao, setConfirmarExclusao] = useState(null)
  const [editando, setEditando] = useState(null)

  const isSuperAdmin = session?.login === SUPER_ADMIN_LOGIN || session?.isSuperAdmin
  const isImpersonating = session?._impersonating === true

  useEffect(() => {
    if (!ready) return
    if (!session) { navigate('/login'); return }
    if (!isSuperAdmin) { navigate('/app'); return }
    loadEmpresas()
  }, [ready, session, isSuperAdmin])

  async function loadEmpresas() {
    setBusy(true)
    try {
      // Tenta via RPC SECURITY DEFINER (bypass RLS para super admin)
      const { data: rpcData, error: rpcError } = await supabase.rpc('listar_empresas_admin')
      if (!rpcError && rpcData) {
        setEmpresas(rpcData)
        setBusy(false)
        return
      }
      // Fallback: query direta (funciona se JWT com is_super_admin ou RLS permissiva)
      const { data, error } = await supabase
        .from('empresas')
        .select('*, usuarios(count)')
        .order('criado_em', { ascending: false })
      if (error) throw error
      setEmpresas(data || [])
    } catch (err) {
      console.error('[SuperAdmin] loadEmpresas erro:', err)
      setToast({ msg: 'Erro ao carregar empresas: ' + err.message, type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  async function toggleEmpresa(empresa) {
    // Verificar se há sessão Supabase válida (super admin local não tem JWT)
    const { data: { session: supaSession } } = await supabase.auth.getSession()
    if (!supaSession) {
      setToast({ msg: 'Erro: Super admin local não pode alterar empresas. Faça login via Supabase Auth.', type: 'error' })
      console.error('[SuperAdmin] toggleEmpresa: Sem sessão Supabase JWT - super admin local não pode fazer UPDATE')
      return
    }

    const novoAtivo = !empresa.ativo
    setBusy(true)
    try {
      console.log('[SuperAdmin] Toggle empresa:', empresa.id, 'novo status:', novoAtivo)
      const { error } = await supabase
        .from('empresas')
        .update({ ativo: novoAtivo })
        .eq('id', empresa.id)
      if (error) {
        console.error('[SuperAdmin] Erro ao atualizar empresa:', error)
        throw error
      }

      const { error: errUsers } = await supabase
        .from('usuarios')
        .update({ ativo: novoAtivo, atualizado_em: Date.now() })
        .eq('empresa_id', empresa.id)
      if (errUsers) console.warn('[SuperAdmin] Erro ao atualizar usuários:', errUsers)

      setToast({ msg: novoAtivo ? 'Empresa ativada.' : 'Empresa desativada e usuários bloqueados.', type: 'success' })
      loadEmpresas()
    } catch (err) {
      console.error('[SuperAdmin] toggleEmpresa erro:', err)
      setToast({ msg: 'Erro: ' + (err.message || err), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  async function salvarNome(empresa, novoNome) {
    if (!novoNome.trim() || novoNome.trim() === empresa.nome) { setEditando(null); return }
    try {
      const { error } = await supabase.from('empresas').update({ nome: novoNome.trim() }).eq('id', empresa.id)
      if (error) throw error
      setToast({ msg: 'Nome atualizado.', type: 'success' })
      setEditando(null)
      loadEmpresas()
    } catch (err) {
      setToast({ msg: err.message, type: 'error' })
    }
  }

  async function excluirEmpresa(empresa) {
    setBusy(true)
    try {
      // RPC com SECURITY DEFINER: deleta todos os dados + auth.users (acesso bloqueado definitivamente)
      const { error } = await supabase.rpc('excluir_empresa_completo', {
        p_empresa_id: empresa.id
      })
      if (error) throw error
      setToast({ msg: 'Empresa, dados e acessos excluídos permanentemente.', type: 'success' })
      setConfirmarExclusao(null)
      loadEmpresas()
    } catch (err) {
      setToast({ msg: err.message, type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  async function criarEmpresa(e) {
    e.preventDefault()
    if (!form.nome.trim() || !form.email.trim()) return
    setBusy(true)
    try {
      const { error } = await supabase.from('empresas').insert({
        id: crypto.randomUUID(),
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        plano: form.plano,
        ativo: true,
        criado_em: Date.now()
      })
      if (error) throw error
      setToast({ msg: 'Empresa criada!', type: 'success' })
      setOpenModal(false)
      setForm({ nome: '', email: '', plano: 'basic' })
      loadEmpresas()
    } catch (err) {
      setToast({ msg: err.message, type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  if (!ready) return (
    <div className="flex h-screen w-screen items-center justify-center bg-brand-900">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
    </div>
  )
  if (!isSuperAdmin) return null

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      {/* Fundo igual as outras páginas */}
      <div className="fixed inset-0 z-0 cm-bg" />
      <div className="fixed inset-0 z-0 cm-bg-overlay" />

      {toast && (
        <div className={`fixed top-4 right-4 z-50 rounded-xl px-4 py-2 text-sm font-semibold shadow-lg ${toast.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'}`}>
          {toast.msg}
          <button className="ml-3 text-white/70" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      <div className="relative z-10 max-w-lg mx-auto px-4 py-6 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">⚙️ Painel Super Admin</h1>
            <p className="text-xs text-white/50">Gerenciamento de empresas</p>
          </div>
          <button
            onClick={async () => { await logout(); navigate('/login') }}
            className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/20"
          >
            Sair
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="cm-card rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold">{empresas.length}</div>
            <div className="text-xs text-white/50 mt-1">Total empresas</div>
          </div>
          <div className="cm-card rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{empresas.filter(e => e.ativo).length}</div>
            <div className="text-xs text-white/50 mt-1">Ativas</div>
          </div>
          <div className="cm-card rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-red-400">{empresas.filter(e => !e.ativo).length}</div>
            <div className="text-xs text-white/50 mt-1">Inativas</div>
          </div>
        </div>

        <button
          onClick={() => setOpenModal(true)}
          className="w-full rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold hover:bg-accent-500"
        >
          + Nova empresa
        </button>

        <div className="space-y-3">
          {busy && <div className="text-center text-sm text-white/50 py-4">Carregando…</div>}
          {empresas.map(emp => (
            <div key={emp.id} className="cm-card rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {editando?.id === emp.id ? (
                    <form onSubmit={e => { e.preventDefault(); salvarNome(emp, editando.nome) }} className="flex gap-1">
                      <input
                        autoFocus
                        value={editando.nome}
                        onChange={e => setEditando(ed => ({ ...ed, nome: e.target.value }))}
                        className="flex-1 rounded-lg bg-white/10 px-2 py-1 text-sm text-white outline-none border border-white/20 min-w-0"
                      />
                      <button type="submit" className="rounded-lg bg-emerald-600 px-2 py-1 text-[10px] font-semibold">✓</button>
                      <button type="button" onClick={() => setEditando(null)} className="rounded-lg bg-white/10 px-2 py-1 text-[10px]">✕</button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="font-semibold text-sm truncate">{emp.nome}</div>
                      <button onClick={() => setEditando({ id: emp.id, nome: emp.nome })} className="text-white/30 hover:text-white/70 text-[10px] shrink-0">✏️</button>
                    </div>
                  )}
                  <div className="text-xs text-white/50 mt-1">{emp.email}</div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full">{emp.plano}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${emp.ativo ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {emp.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                  <div className="text-[10px] text-white/30 mt-1">
                    ID: {emp.id.slice(0, 8)}…
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => { enterEmpresa(emp); navigate('/app') }}
                    className="rounded-lg bg-blue-600/80 hover:bg-blue-600 px-3 py-1.5 text-[10px] font-semibold text-white"
                  >
                    Acessar
                  </button>
                  <button
                    onClick={() => toggleEmpresa(emp)}
                    className={`rounded-lg px-3 py-1.5 text-[10px] font-semibold ${emp.ativo ? 'bg-red-600/80 hover:bg-red-600' : 'bg-emerald-600/80 hover:bg-emerald-600'}`}
                  >
                    {emp.ativo ? 'Desativar' : 'Ativar'}
                  </button>
                  <button
                    onClick={() => setConfirmarExclusao(emp)}
                    className="rounded-lg bg-red-900/80 hover:bg-red-900 border border-red-500/30 px-3 py-1.5 text-[10px] font-semibold text-red-300"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {openModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-sm rounded-2xl bg-brand-800 p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="font-semibold">Nova empresa</h2>
                <button onClick={() => setOpenModal(false)} className="text-white/50 hover:text-white">✕</button>
              </div>
              <form onSubmit={criarEmpresa} className="space-y-3">
                <div>
                  <label className="text-xs text-white/70">Nome da empresa *</label>
                  <input
                    value={form.nome}
                    onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none"
                    placeholder="Ex: Empresa ABC"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/70">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none"
                    placeholder="contato@empresa.com"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/70">Plano</label>
                  <select
                    value={form.plano}
                    onChange={e => setForm(f => ({ ...f, plano: e.target.value }))}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-2 text-sm"
                  >
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-xl bg-accent-600 py-2 text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? 'Criando…' : 'Criar empresa'}
                </button>
              </form>
            </div>
          </div>
        )}

        {confirmarExclusao && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <div className="w-full max-w-sm rounded-2xl cm-card p-6 space-y-4">
              <div className="text-center">
                <div className="text-4xl mb-2">⚠️</div>
                <div className="text-base font-bold text-red-300">Excluir empresa</div>
                <p className="text-xs text-white/70 mt-2">
                  Você está prestes a excluir permanentemente:
                </p>
                <p className="text-sm font-bold text-white mt-1">{confirmarExclusao.nome}</p>
                <p className="text-xs text-red-300 mt-3">
                  Isso apagará <strong>todos os dados</strong> — produtos, funcionários, movimentações, usuários e histórico. <strong>Esta ação não pode ser desfeita.</strong>
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmarExclusao(null)}
                  disabled={busy}
                  className="flex-1 rounded-xl bg-white/10 py-2 text-sm font-semibold hover:bg-white/20"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => excluirEmpresa(confirmarExclusao)}
                  disabled={busy}
                  className="flex-1 rounded-xl bg-red-700 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-red-600"
                >
                  {busy ? 'Excluindo…' : 'Confirmar exclusão'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
